import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAPI } from '../../../Contexts/APIContext'
import { useAuth } from '../../../Contexts/AuthContext'
import { StickyHeader } from '../../../Components/StickyHeader'
import { Breadcrumbs } from '../../../Components/GeneralComponents/Breadcrumbs/Breadcrumbs'
import { ButtonOutline } from '../../../Components/GeneralComponents/ButtonOutline/ButtonOutline'
import TagInputSection from '../../../Components/DashboardComponents/TagInputSection'
import Prompt from '../../../Components/DashboardComponents/Prompt/Prompt'
import Loader from '../../../Components/GeneralComponents/Loader'
import errorToast from '../../../Components/Toasts/ErrorToast'
import successToast from '../../../Components/Toasts/SuccessToast'
import { usePreventFullscreen } from '../../../Helpers/hooks/usePreventFullscreen'
import { convertMillisecondsToSeconds, isPlural } from '../../../services/functions'
import { useGenerateTags } from '../CreateFeel/Step2/useGenerateTags'
import styles from './index.module.css'
import { COLLECTION_REGISTRY } from './collectionData'
import { matchCollections } from './collectionMatcher'

// Constants
const BATCH_SIZE = 50
const MATCH_THRESHOLD = 0.65
const LOAD_MORE_THRESHOLD = 5
const STORAGE_KEY = 'feelsReviewLastDate'

const FeelsReview = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const api = useAPI()
  const { getCurrentUser } = useAuth()
  const videoRef = useRef(null)
  usePreventFullscreen(videoRef)

  // State
  const [feels, setFeels] = useState([])
  const [currentIndex, setCurrentIndex] = useState(0)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const [isPromptDelete, setIsPromptDelete] = useState(false)
  const [reviewedIds, setReviewedIds] = useState(new Set())
  const [suggestedTags, setSuggestedTags] = useState(null)
  const [hideTagged, setHideTagged] = useState(true)
  const [taggedFeelIds, setTaggedFeelIds] = useState(new Set())
  const [filterDate, setFilterDate] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) return stored
    } catch (e) {
      // localStorage may be unavailable in private browsing
    }
    const yesterday = new Date()
    yesterday.setDate(yesterday.getDate() - 1)
    return yesterday.toISOString().split('T')[0]
  })
  const [manualCollection, setManualCollection] = useState(null)
  const [isMuted, setIsMuted] = useState(true)
  const [collectionSearch, setCollectionSearch] = useState('')
  const [showCollectionDropdown, setShowCollectionDropdown] = useState(false)
  const collectionSearchRef = useRef(null)

  // Generate tags via API
  const {
    mutate: generateSuggestedTags,
    isPending: isLoadingGenerateSuggestedTags,
  } = useGenerateTags()

  const handleGenerateTags = (songData) => {
    generateSuggestedTags(songData, {
      onSuccess: (result) => {
        setSuggestedTags(result)
        successToast('Suggested tags generated')
      },
      onError: () => {
        errorToast('Error generating suggested tags')
      },
    })
  }

  // Fetch recent feels
  const fetchFeels = useCallback(async (pageNum = 1, append = false) => {
    try {
      if (append) {
        setLoadingMore(true)
      } else {
        setLoading(true)
        setPage(1)
        setHasMore(true)
      }

      const res = await api.getFeels('published', pageNum, BATCH_SIZE, 'recent')

      if (!res?.feels || !Array.isArray(res.feels)) {
        throw new Error('Invalid response')
      }

      const filterDateObj = new Date(filterDate)
      const filtered = res.feels.filter(feel => {
        const createdDate = new Date(feel.date_created)
        return createdDate >= filterDateObj
      })

      // Determine if more pages exist based on oldest item in batch
      if (res.feels.length < BATCH_SIZE) {
        setHasMore(false)
      } else if (res.feels.length > 0) {
        const oldestInBatch = res.feels[res.feels.length - 1]
        const oldestDate = new Date(oldestInBatch.date_created)
        // Stop if oldest item is before filter date (all subsequent will be older)
        if (oldestDate < filterDateObj) {
          setHasMore(false)
        }
        // Note: if filtered.length === 0 but oldest is still within date range,
        // we continue loading - next batch may have matching feels
      }

      if (append) {
        setFeels(prev => [...prev, ...filtered])
        setPage(pageNum)
      } else {
        setFeels(filtered)
        setCurrentIndex(0)
      }
    } catch (err) {
      console.error('Failed to fetch feels:', err)
      errorToast('Failed to load Feels')
    } finally {
      setLoading(false)
      setLoadingMore(false)
    }
  }, [api, filterDate])

  // Load more feels
  const loadMore = useCallback(() => {
    if (!loadingMore && hasMore) {
      fetchFeels(page + 1, true)
    }
  }, [fetchFeels, page, loadingMore, hasMore])

  useEffect(() => {
    fetchFeels()
  }, [fetchFeels])

  // Save filterDate to localStorage and reset tracking state when it changes
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, filterDate)
    } catch (e) {
      // localStorage may be unavailable in private browsing
    }
    setReviewedIds(new Set())
    setTaggedFeelIds(new Set())
  }, [filterDate])

  const currentFeel = feels[currentIndex]

  // Queries for current feel's tags
  const { data: officialTags, isLoading: isFetchingOfficialTags } = useQuery({
    queryKey: ['officialTags', currentFeel?.f_id],
    queryFn: () => api.getFeelTags({ feelFID: currentFeel?.f_id }),
    enabled: !!currentFeel?.f_id,
    staleTime: 30000, // Cache for 30 seconds
  })

  const { data: userTags, isLoading: isFetchingUserTags } = useQuery({
    queryKey: ['userTags', currentFeel?.f_id],
    queryFn: () => api.getFeelTags({ feelFID: currentFeel?.f_id, isUserTags: true }),
    enabled: !!currentFeel?.f_id,
    staleTime: 30000,
  })

  const { data: suggestedTagsData, isLoading: isFetchingSuggestedTags } = useQuery({
    queryKey: ['suggestedTags', currentFeel?.f_id],
    queryFn: () => api.getFeelTags({ feelFID: currentFeel?.f_id, isSuggestedTags: true }),
    enabled: !!currentFeel?.f_id,
    staleTime: 30000,
  })

  // Fetch all collections from API for dropdown (cached for 10 minutes)
  const { data: apiCollections } = useQuery({
    queryKey: ['allCollections'],
    queryFn: async () => {
      const allCollections = []
      let page = 1
      const pageLimit = 50

      // First request to get total count
      const firstRes = await api.getCollections(page, pageLimit)
      if (firstRes?.lists?.length) {
        allCollections.push(...firstRes.lists)
        const totalCount = firstRes.lists_count || 0
        page++

        // Fetch remaining pages
        while (allCollections.length < totalCount) {
          const res = await api.getCollections(page, pageLimit)
          if (res?.lists?.length) {
            allCollections.push(...res.lists)
            page++
          } else {
            break
          }
        }
      }
      return allCollections
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 30 * 60 * 1000, // Keep in cache for 30 minutes
  })

  // Clear generated tags and manual collection when navigating
  useEffect(() => {
    setSuggestedTags(null)
    setManualCollection(null)
  }, [currentIndex])

  // Use generated tags if available, otherwise fall back to query data
  const effectiveSuggestedTags = suggestedTags || suggestedTagsData

  // Track which feels have tags (for skip logic during navigation)
  useEffect(() => {
    const hasOfficialTags = officialTags?.tags?.length > 0
    if (currentFeel?.f_id && hasOfficialTags) {
      setTaggedFeelIds(prev => {
        if (prev.has(currentFeel.f_id)) return prev
        const newSet = new Set(prev)
        newSet.add(currentFeel.f_id)
        return newSet
      })
    }
  }, [currentFeel?.f_id, officialTags])

  const untaggedCount = useMemo(() =>
    feels.filter(f => !taggedFeelIds.has(f.f_id)).length,
    [feels, taggedFeelIds]
  )

  // Mutations - pass IDs as params to avoid stale closure issues
  const updateTagsMutation = useMutation({
    mutationFn: ({ feelFID, newTags }) => api.updateTagsOnFeel(feelFID, newTags),
    onSuccess: (_, { feelFID }) => {
      queryClient.invalidateQueries({ queryKey: ['officialTags', feelFID] })
      queryClient.invalidateQueries({ queryKey: ['userTags', feelFID] })
      successToast('Tags updated')
    },
    onError: () => {
      errorToast('Failed to update tags')
    }
  })

  const deleteMutation = useMutation({
    mutationFn: (feelFID) => api.deleteFeel(feelFID),
    onSuccess: (_, deletedFID) => {
      setIsPromptDelete(false)
      successToast('Feel deleted')
      setFeels(prev => {
        const newFeels = prev.filter(f => f.f_id !== deletedFID)
        // Adjust currentIndex if needed
        setCurrentIndex(idx => {
          if (idx >= newFeels.length && newFeels.length > 0) {
            return newFeels.length - 1
          }
          return idx
        })
        return newFeels
      })
    },
    onError: () => {
      setIsPromptDelete(false)
      errorToast('Failed to delete Feel')
    }
  })

  const addToCollectionMutation = useMutation({
    mutationFn: ({ collectionFID, feelFID }) => api.addFeelsToCollection(collectionFID, [feelFID]),
    onSuccess: (_, { collectionFID }) => {
      // Look in API collections first, fall back to keyword registry
      const collection = apiCollections?.find(c => c.f_id === collectionFID)
        || COLLECTION_REGISTRY.find(c => c.f_id === collectionFID)
      successToast(`Added to ${collection?.name || 'collection'}`)
    },
    onError: () => {
      errorToast('Failed to add to collection')
    }
  })

  // Report Sync mutation
  const reportSyncMutation = useMutation({
    mutationFn: async (feelFID) => {
      const currentUser = getCurrentUser()
      return api.submitReport({
        report_type: 'NEEDS_SYNC',
        report_text: '',
        object: 'feels',
        object_f_id: feelFID,
        report_origin: 'FAST',
        user_f_id: currentUser?.userId || '',
        report_user_id: currentUser?.email || '',
        department: 'Feels Factory',
        report_data: {
          source: 'feels_review',
        },
      })
    },
    onSuccess: () => {
      successToast('Needs Sync report submitted')
    },
    onError: (e) => {
      errorToast(`Error submitting report: ${e.message ?? ''}`)
    }
  })

  // Navigation
  const goToNext = useCallback(() => {
    if (currentFeel) {
      setReviewedIds(prev => {
        if (prev.has(currentFeel.f_id)) return prev
        const newSet = new Set(prev)
        newSet.add(currentFeel.f_id)
        return newSet
      })
    }

    let nextIndex = currentIndex + 1
    if (hideTagged) {
      while (nextIndex < feels.length && taggedFeelIds.has(feels[nextIndex]?.f_id)) {
        nextIndex++
      }
    }

    if (nextIndex < feels.length) {
      setCurrentIndex(nextIndex)
      if (nextIndex >= feels.length - LOAD_MORE_THRESHOLD && hasMore && !loadingMore) {
        loadMore()
      }
    } else if (hasMore && !loadingMore) {
      // Only load more if there might be untagged feels
      // Prevent infinite loop by checking if we've already loaded and all are tagged
      loadMore()
    } else if (!hasMore && hideTagged) {
      // All loaded feels are tagged and no more to load - stay on current
      successToast('All remaining Feels are tagged!')
    }
  }, [currentFeel, currentIndex, feels, hideTagged, taggedFeelIds, hasMore, loadingMore, loadMore])

  const goToPrev = useCallback(() => {
    let prevIndex = currentIndex - 1
    if (hideTagged) {
      while (prevIndex >= 0 && taggedFeelIds.has(feels[prevIndex]?.f_id)) {
        prevIndex--
      }
    }

    if (prevIndex >= 0) {
      setCurrentIndex(prevIndex)
    }
  }, [currentIndex, feels, hideTagged, taggedFeelIds])

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return

      switch (e.key) {
        case 'ArrowLeft':
          goToPrev()
          break
        case 'ArrowRight':
          goToNext()
          break
        default:
          break
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [goToNext, goToPrev])

  const currentLyrics = useMemo(() => {
    if (!currentFeel) return ''
    return currentFeel.lyrics || currentFeel.lyrics_sync?.map(l => l.line).join(' ') || ''
  }, [currentFeel])

  const collectionMatches = useMemo(() => {
    if (!currentFeel || !currentLyrics) return []
    return matchCollections(currentLyrics, COLLECTION_REGISTRY, MATCH_THRESHOLD)
  }, [currentFeel, currentLyrics])

  // Memoize dropdown options (API collections not already matched)
  const availableCollections = useMemo(() => {
    if (!apiCollections) return []
    return apiCollections
      .filter(c => !collectionMatches.some(m => m.f_id === c.f_id))
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
  }, [apiCollections, collectionMatches])

  // Filter collections based on search
  const filteredCollections = useMemo(() => {
    if (!collectionSearch.trim()) return availableCollections
    const term = collectionSearch.toLowerCase()
    return availableCollections.filter(c =>
      (c.name || '').toLowerCase().includes(term)
    )
  }, [availableCollections, collectionSearch])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (collectionSearchRef.current && !collectionSearchRef.current.contains(e.target)) {
        setShowCollectionDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const breadcrumbItems = useMemo(() => [
    { text: 'Dashboard', onClick: () => navigate('/dashboard') },
    { text: 'Feels Review' }
  ], [navigate])

  // Check if PREV button should be disabled (accounts for hideTagged mode)
  const isPrevDisabled = useMemo(() => {
    if (currentIndex === 0) return true
    if (!hideTagged) return false
    // Check if any untagged feel exists before current
    for (let i = currentIndex - 1; i >= 0; i--) {
      if (!taggedFeelIds.has(feels[i]?.f_id)) return false
    }
    return true
  }, [currentIndex, hideTagged, taggedFeelIds, feels])

  if (loading) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.loadingContainer}>
          <Loader isLoading={true} />
          <p>Loading Feels...</p>
        </div>
      </div>
    )
  }

  if (feels.length === 0) {
    return (
      <div className={styles.wrapper}>
        <StickyHeader>
          <Breadcrumbs items={breadcrumbItems} />
          <div className={styles.titleContainer}>
            <h1 className={styles.title}>Feels Review</h1>
          </div>
        </StickyHeader>
        <div className={styles.emptyState}>
          <p>No new Feels since {filterDate}</p>
          <div className={styles.dateFilter}>
            <label>Show Feels since:</label>
            <input
              type="date"
              value={filterDate}
              onChange={(e) => setFilterDate(e.target.value)}
            />
            <ButtonOutline color="green" size="small" text="REFRESH" onClick={() => fetchFeels()} />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.wrapper}>
      {isPromptDelete && (
        <Prompt
          title="Delete Feels"
          subtitle="Are you sure you want to delete this Feels? This action is permanent."
          primaryAction="CANCEL"
          onPrimaryClick={() => setIsPromptDelete(false)}
          secondaryAction="DELETE"
          onSecondaryClick={() => deleteMutation.mutate(currentFeel?.f_id)}
          isSecondaryActionDestructive={true}
          onCloseClick={() => setIsPromptDelete(false)}
        />
      )}

      <div className={styles.explainerBanner}>
        Many of these Feels were created by real users. Review them, add tags, assign to collections, or delete bad ones.
        <br />
        Deleting removes the Feel from users who saved it, which can be a jarring experience — use sparingly.
      </div>

      <StickyHeader>
        <Breadcrumbs items={breadcrumbItems} />
        <div className={styles.titleRow}>
          <h1 className={styles.title}>Feels Review</h1>
          <div className={styles.dateFilterBar}>
            <label htmlFor="filterDate">Since:</label>
            <input
              id="filterDate"
              type="date"
              value={filterDate}
              onChange={(e) => setFilterDate(e.target.value)}
            />
            <ButtonOutline color="pink" size="small" text="REFRESH" onClick={() => fetchFeels()} />
            <label className={`${styles.hideTaggedToggle} ${hideTagged ? styles.active : ''}`}>
              <input
                type="checkbox"
                checked={hideTagged}
                onChange={(e) => setHideTagged(e.target.checked)}
              />
              <span>Skip tagged</span>
              {hideTagged && taggedFeelIds.size > 0 && (
                <span>({untaggedCount} left)</span>
              )}
            </label>
            <div className={styles.progress}>
              {currentIndex + 1} of {feels.length}{hasMore && '+'}
              {reviewedIds.size > 0 && ` (${reviewedIds.size} reviewed)`}
              {loadingMore && ' Loading more...'}
            </div>
          </div>
        </div>
      </StickyHeader>

      <div className={styles.songNavRow}>
        <div className={styles.feelInfo}>
          <a
            href={`/song/${currentFeel?.song_f_id}`}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.songTitleLink}
          >
            <h2 className={styles.songTitle}>{currentFeel?.song_name}</h2>
          </a>
          <a
            href={`/artist/${currentFeel?.artist?.artist_name}`}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.artistNameLink}
          >
            <h3 className={styles.artistName}>{currentFeel?.song_artist_name}</h3>
          </a>
          <a
            href={`/feel/${currentFeel?.f_id}`}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.feelId}
          >
            {currentFeel?.f_id}
          </a>
        </div>

        <div className={styles.navButtonsTop}>
          <ButtonOutline
            color="red"
            size="small"
            text="DELETE"
            onClick={() => setIsPromptDelete(true)}
          />
          <ButtonOutline
            color="pink"
            size="small"
            text={reportSyncMutation.isPending ? 'REPORTING...' : 'REPORT SYNC'}
            disabled={reportSyncMutation.isPending}
            onClick={() => reportSyncMutation.mutate(currentFeel?.f_id)}
          />
          <ButtonOutline
            color="pink"
            size="small"
            text="← PREV"
            disabled={isPrevDisabled}
            onClick={goToPrev}
          />
          <ButtonOutline
            color={currentIndex >= feels.length - 1 && !hasMore ? 'gray' : 'green'}
            size="small"
            text={
              currentIndex >= feels.length - 1
                ? (hasMore
                    ? (loadingMore ? 'LOADING...' : 'LOAD MORE')
                    : 'ALL DONE ✓')
                : 'NEXT →'
            }
            disabled={currentIndex >= feels.length - 1 && !hasMore}
            onClick={goToNext}
          />
        </div>
      </div>

      <div className={styles.contentWrapper}>
        {/* LEFT: Video */}
        <div className={styles.feelsColumn}>

          <div className={styles.videoContainer}>
            <video
              ref={videoRef}
              key={currentFeel?.f_id}
              className={styles.videoPlayer}
              controls
              autoPlay
              muted={isMuted}
              loop
              src={currentFeel?.preview?.video?.video?.url}
              controlsList="nofullscreen"
              onVolumeChange={(e) => setIsMuted(e.target.muted)}
            />
          </div>

          <div className={styles.lyrics}>
            <ul className={styles.lyricsText}>
              {currentFeel?.lyrics_sync?.map(({ line }, index) => (
                <li key={`${line}-${index}`}>{line}</li>
              ))}
            </ul>
            {currentFeel?.video_duration && (
              <p className={styles.duration}>
                {convertMillisecondsToSeconds(currentFeel.video_duration)} second
                {isPlural(convertMillisecondsToSeconds(currentFeel.video_duration))}
              </p>
            )}
          </div>
        </div>

        {/* CENTER: Tags */}
        <div className={styles.tagsColumn}>
          <TagInputSection
            key={currentFeel?.f_id}
            disabled={updateTagsMutation.isPending || isFetchingOfficialTags || isFetchingUserTags}
            officialTags={officialTags?.tags}
            userTags={userTags?.tags_user}
            suggestedTags={effectiveSuggestedTags?.tags || []}
            handleTagsUpdated={(newTags) => updateTagsMutation.mutate({ feelFID: currentFeel?.f_id, newTags })}
            isFetchingOfficialTags={isFetchingOfficialTags}
            isFetchingUserTags={isFetchingUserTags}
            isFetchingSuggestedTags={isFetchingSuggestedTags || isLoadingGenerateSuggestedTags}
            handleSuggestionTagClick={{
              mutate: (newTags) => updateTagsMutation.mutate({ feelFID: currentFeel?.f_id, newTags }),
              isPending: updateTagsMutation.isPending,
            }}
            refetchSuggestedTags={handleGenerateTags}
            song={currentFeel}
            isTagsUpdatingSuccess={updateTagsMutation.isSuccess}
          />
        </div>

        {/* RIGHT: Collections */}
        <div className={styles.collectionsColumn}>
          <h4 className={styles.sectionTitle}>Suggested Collections</h4>

          {/* Manual collection selector */}
          <div className={styles.manualSelector} ref={collectionSearchRef}>
            <input
              type="text"
              className={styles.collectionSearchInput}
              placeholder="Search collections..."
              value={collectionSearch}
              onChange={(e) => setCollectionSearch(e.target.value)}
              onFocus={() => setShowCollectionDropdown(true)}
            />
            {showCollectionDropdown && (
              <div className={styles.collectionDropdownList}>
                {filteredCollections.length === 0 ? (
                  <div className={styles.noCollectionResults}>No collections found</div>
                ) : (
                  filteredCollections.map(c => (
                    <div
                      key={c.f_id}
                      className={styles.collectionDropdownItem}
                      onClick={() => {
                        setManualCollection(c)
                        setCollectionSearch('')
                        setShowCollectionDropdown(false)
                      }}
                    >
                      {c.name}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          {/* Manual selection (shown at top when selected) */}
          {manualCollection && (
            <div className={`${styles.collectionItem} ${styles.manualSelection}`}>
              <div className={styles.collectionInfo}>
                <span className={styles.collectionName}>{manualCollection.name}</span>
                <button
                  className={styles.clearManual}
                  onClick={() => setManualCollection(null)}
                >
                  ✕
                </button>
              </div>
              <span className={styles.manualLabel}>Manual selection</span>
              <ButtonOutline
                color="green"
                size="small"
                text={addToCollectionMutation.isPending && addToCollectionMutation.variables?.collectionFID === manualCollection.f_id ? '...' : 'ADD'}
                disabled={addToCollectionMutation.isPending}
                onClick={() => {
                  addToCollectionMutation.mutate({ collectionFID: manualCollection.f_id, feelFID: currentFeel?.f_id })
                  setManualCollection(null)
                }}
              />
            </div>
          )}

          {collectionMatches.length > 0 ? (
            <div className={styles.collectionsList}>
              {collectionMatches.map((match) => (
                <div key={match.f_id} className={styles.collectionItem}>
                  <div className={styles.collectionInfo}>
                    <span className={styles.collectionName}>{match.name}</span>
                    <span className={styles.matchScore}>{Math.round(match.score * 100)}%</span>
                  </div>
                  <span className={styles.matchedKeyword}>&quot;{match.matchedKeyword}&quot;</span>
                  <ButtonOutline
                    color="green"
                    size="small"
                    text={addToCollectionMutation.isPending && addToCollectionMutation.variables?.collectionFID === match.f_id ? '...' : 'ADD'}
                    disabled={addToCollectionMutation.isPending}
                    onClick={() => addToCollectionMutation.mutate({ collectionFID: match.f_id, feelFID: currentFeel?.f_id })}
                  />
                </div>
              ))}
            </div>
          ) : (
            !manualCollection && <p className={styles.noMatches}>No collection matches found</p>
          )}
        </div>
      </div>

      <div className={styles.keyboardHints}>
        Keyboard: ← Prev | → Next
      </div>
    </div>
  )
}

export default FeelsReview
